import { useState } from "react";
import { routes } from "../../scripts/public-content";
import { customerJourney } from "../../scripts/public-journey";
import type { MirrorResult } from "../shared/product";
import { publicPost as post } from "./lib/api";

export function PublicStoryPage() {
  const normalizedPath = location.pathname.length > 1 ? location.pathname.replace(/\/$/, "") : location.pathname;
  const content = routes.find(route => route.path === normalizedPath) ?? routes[0]!;
  const currentJourneyIndex = customerJourney.findIndex(item => item.path === normalizedPath);
  const nextChapter = currentJourneyIndex >= 0 && currentJourneyIndex < customerJourney.length - 1
    ? customerJourney[currentJourneyIndex + 1]
    : null;
  const [will, setWill] = useState(content.exampleWill);
  const [statement, setStatement] = useState(content.exampleWord);
  const [result, setResult] = useState<MirrorResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      setResult(await post<MirrorResult>("/api/v1/mirror", {
        will,
        statement,
        register: "practical",
        locale: "en",
        attribution: Object.fromEntries(new URLSearchParams(location.search)),
      }));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The Mirror could not respond.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="story-page">
      <header className="story-header">
        <a className="story-brand" href="/" aria-label="WORD & WILL home">
          <img className="story-brand-mark" src="/icons/wordwill.svg" alt="" />
          <span>WORD <em>&amp;</em> WILL</span>
        </a>
        <nav aria-label="Public navigation">
          <a href="/privacy">Privacy</a>
          <a href="/pricing">Membership</a>
          <a href="/login">Sign in</a>
        </nav>
      </header>

      <StoryJourney currentPath={normalizedPath} />

      <article className="story-shell">
        <section className="story-hero" aria-labelledby="story-title">
          {currentJourneyIndex >= 0 && <p className="story-chapter-count">Chapter {currentJourneyIndex + 1} of {customerJourney.length}</p>}
          <p className="story-eyebrow">{content.chapter}</p>
          <h1 id="story-title">{content.h1}</h1>
          <p className="story-lede">{content.answer}</p>
        </section>

        <section className="story-beats" aria-label="The story">
          {content.beats.map((beat, index) => (
            <article className="story-beat" key={beat.title}>
              <span>{String(index + 1).padStart(2, "0")} · {beat.label}</span>
              <h2>{beat.title}</h2>
              <p>{beat.body}</p>
            </article>
          ))}
        </section>

        <aside className="story-outcome">
          <span>THE POSSIBILITY</span>
          <p>{content.outcome}</p>
        </aside>

        <section className="story-dialogue" aria-labelledby="story-dialogue-title">
          <div className="story-dialogue-heading">
            <p className="story-eyebrow">A MIRROR MOMENT</p>
            <h2 id="story-dialogue-title">A real sentence. A visible choice.</h2>
          </div>
          <div className="story-dialogue-card">
            <div><span>MY WILL</span><p>{content.exampleWill}</p></div>
            <div><span>MY WORD</span><blockquote>“{content.exampleWord}”</blockquote></div>
            <div className="is-mirror"><span>THE MIRROR</span><p>{content.exampleMirror}</p></div>
            <div className="is-correction"><span>A CORRECTION</span><p>“{content.exampleCorrection}”</p></div>
          </div>
        </section>

        <section className="story-try" aria-labelledby="story-try-title">
          <div className="story-try-copy">
            <p className="story-eyebrow">TRY YOUR OWN WORDS</p>
            <h2 id="story-try-title">What are you saying without hearing yourself?</h2>
            <p>Give The Mirror one Will and one real sentence. It will look for the relationship between them. The public Mirror does not use what your private Mirror remembers.</p>
          </div>
          <div className="mirror-box story-mirror-box">
            <label>
              What do you want?
              <textarea value={will} onChange={event => setWill(event.target.value)} placeholder="I want to…" />
            </label>
            <label>
              What did you hear yourself say?
              <textarea value={statement} onChange={event => setStatement(event.target.value)} placeholder="The actual sentence…" />
            </label>
            <button onClick={submit} disabled={busy || !will.trim() || !statement.trim()}>{busy ? "Looking…" : content.cta}</button>
            {error && <p className="story-error" role="alert">{error}</p>}
            {result && <PublicResult result={result} />}
          </div>
        </section>

        <section className="story-final">
          <p>The Mirror never judges. It reveals.</p>
          {nextChapter ? (
            <>
              <h2>Keep following the story.</h2>
              <a className="story-primary" href={nextChapter.path}>Next: {nextChapter.label} →</a>
            </>
          ) : normalizedPath === "/pricing" ? (
            <>
              <h2>Start with one useful Mirror. Decide about Membership after you feel the difference.</h2>
              <a className="story-primary" href="/start">Start with your free Mirror</a>
            </>
          ) : (
            <>
              <h2>You decide what the next Word will be.</h2>
              <a className="story-primary" href="/start">Meet Your Mirror</a>
            </>
          )}
        </section>
      </article>

      <footer className="story-footer">
        <span>WORD &amp; WILL</span>
        <nav aria-label="Footer">
          {customerJourney.map(item => <a href={item.path} key={item.path}>{item.label}</a>)}
        </nav>
      </footer>
    </main>
  );
}

function StoryJourney({ currentPath }: { currentPath: string }) {
  return (
    <nav className="sales-journey" aria-label="WORD & WILL story">
      {customerJourney.map((item, index) => (
        <a className={item.path === currentPath ? "is-current" : undefined} href={item.path} key={item.path} aria-current={item.path === currentPath ? "page" : undefined}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <strong>{item.label}</strong>
        </a>
      ))}
    </nav>
  );
}

function PublicResult({ result }: { result: MirrorResult }) {
  return (
    <div className={`result ${result.result_type}`} aria-live="polite">
      {result.result_type === "safety" ? (
        <><h2>This is not an ordinary Mirror moment.</h2><p>{result.mirror}</p></>
      ) : result.result_type === "contradiction" ? (
        <><blockquote>{result.statement}</blockquote><p>{result.mirror}</p><strong>{result.question}</strong>{result.continuation && <a className="button secondary" href={result.continuation.url}>{result.continuation.label}</a>}</>
      ) : result.result_type === "aligned" ? (
        <><h2>{result.mirror}</h2><p>{result.alignment_explanation}</p>{result.continuation && <a className="button secondary" href={result.continuation.url}>{result.continuation.label}</a>}</>
      ) : (
        <><p>{result.mirror}</p><strong>{result.question}</strong></>
      )}
    </div>
  );
}
